import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Avatar, Chip, IconButton, Button, TextField,
  Tooltip, Alert, Skeleton, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Switch, FormControlLabel, Divider,
} from '@mui/material';
import {
  AddOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  PeopleAltOutlined, CheckCircleOutlined, RadioButtonUncheckedOutlined,
  LockOutlined, ChevronLeftOutlined, ChevronRightOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { api } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import SlidePanel from '../../components/SlidePanel';

const PRIMARY   = '#1075BA';
const PAGE_SIZE = 10;

const EMPTY = { firstName: '', lastName: '', email: '', password: '', organizationId: '', roleId: '', jobTitle: '', department: '', isActive: true };

function InitialsAvatar({ u }) {
  const initials = `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase();
  const hue = ((u.firstName?.charCodeAt(0) || 65) * 13) % 360;
  return (
    <Avatar sx={{ width: 34, height: 34, fontSize: '0.8rem', fontWeight: 700, bgcolor: `hsl(${hue},50%,88%)`, color: `hsl(${hue},50%,35%)` }}>
      {initials || '?'}
    </Avatar>
  );
}

export default function UsersTab({ hideTitle = false }) {
  const [users, setUsers]     = useState([]);
  const [orgs, setOrgs]       = useState([]);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [page, setPage]         = useState(1);
  const [error, setError]       = useState('');
  const { user: me } = useAuth();

  // Panel state
  const [panel, setPanel]     = useState(null); // null | 'form' | 'delete'
  const [target, setTarget]   = useState(null);
  const [formState, setFormState] = useState(EMPTY);
  const [formError, setFormError] = useState('');
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [uRes, oRes, rRes] = await Promise.all([api.get('/admin-users'), api.get('/organizations'), api.get('/roles')]);
      setUsers(uRes.data.data || []);
      setOrgs(oRes.data.data || []);
      setRoles(rRes.data.data || []);
    } catch (e) { setError(e?.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, orgFilter]);

  const filtered    = users.filter(u => {
    const q = search.toLowerCase();
    const ms = !search || `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    const mo = !orgFilter || u.organization?._id === orgFilter;
    return ms && mo;
  });
  const totalPages  = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated   = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const orgRoles = roles.filter(r => r.organization?._id === formState.organizationId);

  const openAdd = () => {
    setTarget(null); setFormState(EMPTY); setFormError(''); setSaving(false); setPanel('form');
  };
  const openEdit = (u) => {
    setTarget(u);
    setFormState({
      firstName: u.firstName || '', lastName: u.lastName || '',
      email: u.email || '', password: '',
      organizationId: u.organization?._id || '', roleId: u.role?._id || '',
      jobTitle: u.jobTitle || '', department: u.department || '',
      isActive: u.isActive !== false,
    });
    setFormError(''); setSaving(false); setPanel('form');
  };
  const openDel = (u) => { setTarget(u); setPanel('delete'); };
  const closePanel = () => setPanel(null);

  const set = (k, v) => setFormState(p => ({ ...p, [k]: v }));
  const isEdit = !!target?._id;

  const handleSave = async () => {
    if (!formState.firstName || !formState.lastName || !formState.email || !formState.organizationId || !formState.roleId)
      { setFormError('All fields except password are required'); return; }
    if (!isEdit && !formState.password) { setFormError('Password is required for new users'); return; }
    setSaving(true); setFormError('');
    try {
      const payload = { ...formState };
      if (!payload.password) delete payload.password;
      const { data } = isEdit
        ? await api.put(`/admin-users/${target._id}`, payload)
        : await api.post('/admin-users', payload);
      setUsers(prev => isEdit ? prev.map(u => u._id === data.data._id ? data.data : u) : [data.data, ...prev]);
      closePanel();
    } catch (e) { setFormError(e?.response?.data?.message || 'Save failed'); setSaving(false); }
  };

  const handleDelete = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.delete(`/admin-users/${target._id}`);
      setUsers(prev => prev.filter(u => u._id !== target._id));
      closePanel();
    } catch (e) { setFormError(e?.response?.data?.message || 'Delete failed'); setSaving(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        {!hideTitle && (
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: 'text.primary' }}>Admin Users</Typography>
            <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${users.length} admin user${users.length !== 1 ? 's' : ''}`}</Typography>
          </Box>
        )}
        {hideTitle && (
          <Typography variant="body2" color="text.secondary">{loading ? 'Loading…' : `${users.length} admin user${users.length !== 1 ? 's' : ''}`}</Typography>
        )}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField placeholder="Search users…" size="small" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 17 }} /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Company</InputLabel>
            <Select value={orgFilter} label="Company" onChange={e => setOrgFilter(e.target.value)}>
              <MenuItem value="">All companies</MenuItem>
              {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="contained" startIcon={<AddOutlined />} onClick={openAdd} sx={{ borderRadius: 2 }}>New User</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Card sx={{ borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ p: 2.5 }}>{[...Array(5)].map((_, i) => <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}><Skeleton variant="circular" width={34} height={34} /><Box sx={{ flex: 1 }}><Skeleton width="40%" /><Skeleton width="28%" height={14} /></Box><Skeleton width={80} /><Skeleton width={60} height={20} sx={{ borderRadius: 1 }} /></Box>)}</Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}><PeopleAltOutlined sx={{ fontSize: 48, color: alpha(PRIMARY, 0.2), mb: 2 }} /><Typography color="text.secondary">{search || orgFilter ? 'No users match.' : 'No admin users yet.'}</Typography></Box>
        ) : (
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '28%' }}>User</TableCell>
                  <TableCell sx={{ width: '16%' }}>Company</TableCell>
                  <TableCell sx={{ width: '14%' }}>Role</TableCell>
                  <TableCell sx={{ width: '14%' }}>Job Title</TableCell>
                  <TableCell sx={{ width: 60 }}>Active</TableCell>
                  <TableCell sx={{ width: '14%' }}>Last Login</TableCell>
                  <TableCell sx={{ width: 90 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map(u => (
                  <TableRow key={u._id} hover sx={{ '&:hover': { background: alpha(PRIMARY, 0.025) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <InitialsAvatar u={u} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                            {u.firstName} {u.lastName}
                            {u._id === me?._id && <Chip label="You" size="small" sx={{ ml: 0.75, height: 16, fontSize: '0.62rem', fontWeight: 700, background: alpha(PRIMARY, 0.1), color: PRIMARY, '& .MuiChip-label': { px: 0.75 } }} />}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{u.organization?.name || '—'}</Typography></TableCell>
                    <TableCell>
                      {u.role
                        ? <Chip label={u.role.name} size="small" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600, background: alpha(u.role.color || PRIMARY, 0.1), color: u.role.color || PRIMARY, border: `1px solid ${alpha(u.role.color || PRIMARY, 0.2)}` }} />
                        : '—'}
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{u.jobTitle || '—'}</Typography></TableCell>
                    <TableCell>
                      {u.isActive ? <CheckCircleOutlined sx={{ fontSize: 18, color: '#00a882' }} /> : <RadioButtonUncheckedOutlined sx={{ fontSize: 18, color: alpha('#000', 0.2) }} />}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => openEdit(u)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                        </Tooltip>
                        <Tooltip title={u._id === me?._id ? "Can't delete yourself" : 'Delete user'}>
                          <span>
                            <IconButton size="small" disabled={u._id === me?._id} onClick={() => openDel(u)} sx={{ '&:hover:not(:disabled)': { color: '#d32f2f' } }}>
                              <DeleteOutlined sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && filtered.length > 0 && totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1.5, borderTop: `1px solid rgba(16,117,186,0.07)` }}>
            <IconButton size="small" disabled={page === 1} onClick={() => setPage(p => p - 1)}
              sx={{ border: `1px solid rgba(16,117,186,0.2)`, borderRadius: 1.5, width: 28, height: 28, '&:hover:not(:disabled)': { background: 'rgba(16,117,186,0.06)' } }}>
              <ChevronLeftOutlined sx={{ fontSize: 16 }} />
            </IconButton>
            {[...Array(totalPages)].map((_, i) => {
              const p = i + 1;
              const show = p === 1 || p === totalPages || Math.abs(p - page) <= 1;
              const isEllipsis = !show && (p === 2 || p === totalPages - 1);
              if (isEllipsis) return <Typography key={p} sx={{ color: 'text.secondary', fontSize: '0.78rem', px: 0.25 }}>…</Typography>;
              if (!show) return null;
              return (
                <Box key={p} onClick={() => setPage(p)} sx={{ width: 28, height: 28, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '0.78rem', fontWeight: p === page ? 700 : 500, color: p === page ? '#fff' : 'text.secondary', background: p === page ? '#1075BA' : 'transparent', border: `1px solid ${p === page ? '#1075BA' : 'rgba(16,117,186,0.2)'}`, transition: 'all 0.12s', '&:hover': { background: p === page ? '#1075BA' : 'rgba(16,117,186,0.06)' } }}>
                  {p}
                </Box>
              );
            })}
            <IconButton size="small" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}
              sx={{ border: `1px solid rgba(16,117,186,0.2)`, borderRadius: 1.5, width: 28, height: 28, '&:hover:not(:disabled)': { background: 'rgba(16,117,186,0.06)' } }}>
              <ChevronRightOutlined sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
      </Card>

      {/* Add / Edit slide panel */}
      <SlidePanel
        open={panel === 'form'}
        onClose={closePanel}
        title={isEdit ? 'Edit User' : 'Create Admin User'}
        subtitle={isEdit ? `Editing ${target?.firstName} ${target?.lastName}` : 'Fill in the details to create a new admin'}
        footer={
          <>
            <Button onClick={closePanel} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ borderRadius: 2 }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create User'}
            </Button>
          </>
        }
      >
        {formError && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>{formError}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Personal Info</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <TextField label="First Name *" value={formState.firstName} onChange={e => set('firstName', e.target.value)} fullWidth size="small" />
                <TextField label="Last Name *"  value={formState.lastName}  onChange={e => set('lastName',  e.target.value)} fullWidth size="small" />
              </Box>
              <TextField label="Email *" type="email" value={formState.email} onChange={e => set('email', e.target.value)} fullWidth size="small" />
            </Box>
          </Box>

          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>
              {isEdit ? 'Change Password' : 'Password *'}
            </Typography>
            <TextField
              label={isEdit ? 'New Password (leave blank to keep)' : 'Password *'}
              type="password" value={formState.password} onChange={e => set('password', e.target.value)}
              fullWidth size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlined sx={{ fontSize: 18, color: alpha(PRIMARY, 0.45) }} /></InputAdornment> }}
            />
          </Box>

          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Assignment</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Company *</InputLabel>
                <Select value={formState.organizationId} label="Company *" onChange={e => { set('organizationId', e.target.value); set('roleId', ''); }}>
                  {orgs.map(o => <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth disabled={!formState.organizationId}>
                <InputLabel>Role *</InputLabel>
                <Select value={formState.roleId} label="Role *" onChange={e => set('roleId', e.target.value)}>
                  {orgRoles.map(r => (
                    <MenuItem key={r._id} value={r._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: r.color || '#888', flexShrink: 0 }} />
                        {r.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />

          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1, display: 'block' }}>Work Info</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField label="Job Title" value={formState.jobTitle || ''} onChange={e => set('jobTitle', e.target.value)} fullWidth size="small" />
              <TextField label="Department" value={formState.department || ''} onChange={e => set('department', e.target.value)} fullWidth size="small" />
            </Box>
          </Box>

          {isEdit && (
            <>
              <Divider sx={{ borderColor: alpha(PRIMARY, 0.08) }} />
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5, display: 'block' }}>Status</Typography>
                <FormControlLabel
                  control={<Switch checked={formState.isActive !== false} onChange={e => set('isActive', e.target.checked)} size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00a882' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: '#00a882' } }} />}
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{formState.isActive !== false ? 'Active' : 'Inactive'}</Typography>}
                />
              </Box>
            </>
          )}
        </Box>
      </SlidePanel>

      {/* Delete slide panel */}
      <SlidePanel open={panel === 'delete'} onClose={closePanel} title="Delete User" subtitle="This action cannot be undone" width={420}
        footer={
          <>
            <Button onClick={closePanel} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button onClick={handleDelete} variant="contained" color="error" disabled={saving} sx={{ borderRadius: 2, fontWeight: 700 }}>
              {saving ? 'Deleting…' : 'Yes, Delete User'}
            </Button>
          </>
        }
      >
        {formError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{formError}</Alert>}
        <Box sx={{ background: alpha('#d32f2f', 0.05), border: `1px solid ${alpha('#d32f2f', 0.15)}`, borderRadius: 2.5, p: 2.5 }}>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
            You are about to permanently delete <strong>{target?.firstName} {target?.lastName}</strong> ({target?.email}).
            This action <strong>cannot be undone</strong>.
          </Typography>
        </Box>
      </SlidePanel>
    </Box>
  );
}